import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import {
  ArrowRight,
  Sparkles,
  Award,
  Briefcase,
  GraduationCap,
  Users,
  CheckCircle2,
  PenLine,
  BookOpen,
  ScrollText,
  Gavel,
  Scale,
  Search,
  Target,
  Eye,
  Flag,
  ShieldAlert,
} from "lucide-react";
import { listPublishedSimulations } from "@/lib/api/lxp-simulations.functions";

const featuredQO = queryOptions({
  queryKey: ["lxp", "featured"],
  queryFn: () => listPublishedSimulations({ data: { limit: 3 } }),
});

export const Route = createFileRoute("/lexpractiq/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(featuredQO),
  head: () => ({
    meta: [
      { title: "SA LexPractiq — Legal Experience Platform | Learn by Doing" },
      {
        name: "description",
        content:
          "South Africa's AI-powered Legal Experience Platform. Complete realistic legal simulations, build a verified portfolio and launch your legal career.",
      },
      { property: "og:title", content: "SA LexPractiq — Legal Experience Platform" },
      {
        property: "og:description",
        content:
          "Learn by doing. Build your portfolio. Launch your legal career. AI-powered legal simulations for students, graduates and professionals.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: LandingPage,
});

function LandingPage() {
  const { data } = useSuspenseQuery(featuredQO);
  return (
    <>
      {/* Hero */}
      <section className="lxp-container" style={{ padding: "5rem 1.25rem 3rem", textAlign: "center" }}>
        <span className="lxp-badge" style={{ marginBottom: "1.25rem" }}>
          <Sparkles size={12} /> AI-powered Legal Experience Platform
        </span>
        <h1
          style={{
            fontSize: "clamp(2.2rem, 5vw, 3.8rem)",
            lineHeight: 1.05,
            letterSpacing: "-0.02em",
            fontWeight: 800,
            margin: "0 0 1.25rem",
            maxWidth: 900,
            marginInline: "auto",
          }}
        >
          Learn by Doing. Build Your Portfolio.{" "}
          <span
            style={{
              background: "linear-gradient(90deg, var(--lxp-accent), #38bdf8)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Launch Your Legal Career.
          </span>
        </h1>
        <p className="lxp-muted" style={{ fontSize: "1.1rem", maxWidth: 720, margin: "0 auto 2rem" }}>
          SA LexPractiq places you inside realistic legal workplaces. Complete practical tasks with an AI supervising
          attorney, earn verified certificates, and get discovered by employers.
        </p>
        <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
          <Link to="/lexpractiq/simulations" className="lxp-btn">
            Browse simulations <ArrowRight size={16} />
          </Link>
          <Link to="/lexpractiq/how-it-works" className="lxp-btn-ghost">
            How it works
          </Link>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "2rem",
            marginTop: "2.5rem",
            flexWrap: "wrap",
            fontSize: "0.85rem",
            color: "var(--lxp-text-muted)",
          }}
        >
          <span>✓ Real SA case scenarios</span>
          <span>✓ AI supervising attorney</span>
          <span>✓ Verified digital portfolio</span>
          <span>✓ Employer-ready certificates</span>
        </div>
      </section>

      {/* Focus areas — legal writing, research, case summaries */}
      <section className="lxp-container" style={{ padding: "3rem 1.25rem" }}>
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <span className="lxp-badge" style={{ marginBottom: "0.75rem" }}>What we focus on</span>
          <h2 style={{ fontSize: "1.8rem", margin: "0 0 0.5rem", letterSpacing: "-0.01em" }}>
            The craft of practical lawyering
          </h2>
          <p className="lxp-muted" style={{ margin: "0 auto", maxWidth: 640, fontSize: "0.95rem" }}>
            Every simulation is designed to sharpen the four skills employers keep telling us are missing when graduates arrive:
            writing, research, analysis and professional judgment.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1rem" }}>
          {[
            {
              icon: <PenLine size={20} />,
              title: "Legal writing",
              body: "Draft letters of demand, heads of argument, opinions and pleadings with rubric-based feedback on structure, tone and authority.",
            },
            {
              icon: <Search size={20} />,
              title: "Legal research",
              body: "Learn to find, read and apply South African statutes, case law and secondary sources — with OSCOLA-compliant citations.",
            },
            {
              icon: <ScrollText size={20} />,
              title: "Case summaries",
              body: "Distil long judgments into crisp facts, issues, ratio and obiter using the IRAC/FIRAC method used by chambers and firms.",
            },
            {
              icon: <BookOpen size={20} />,
              title: "Legal opinions & memos",
              body: "Practice giving clear, defensible advice on real client scenarios — the number one deliverable in candidate-attorney work.",
            },
            {
              icon: <Gavel size={20} />,
              title: "Court & tribunal skills",
              body: "CCMA referrals, bail applications, magistrate motion work and constitutional matters — with the correct forms and procedure.",
            },
            {
              icon: <Scale size={20} />,
              title: "Ethics & professionalism",
              body: "Every submission is scored on client-care, confidentiality and Legal Practice Council rules — not just black-letter law.",
            },
          ].map((f) => (
            <div key={f.title} className="lxp-card">
              <div
                style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: "var(--lxp-accent-glow)", color: "var(--lxp-accent)",
                  display: "grid", placeItems: "center", marginBottom: "0.75rem",
                }}
              >
                {f.icon}
              </div>
              <h3 style={{ margin: "0 0 0.4rem", fontSize: "1rem" }}>{f.title}</h3>
              <p className="lxp-muted" style={{ margin: 0, fontSize: "0.85rem", lineHeight: 1.55 }}>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* About: mission, vision, goals */}
      <section className="lxp-container" style={{ padding: "3rem 1.25rem" }}>
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <span className="lxp-badge" style={{ marginBottom: "0.75rem" }}>About SA LexPractiq</span>
          <h2 style={{ fontSize: "1.8rem", margin: "0 0 0.5rem", letterSpacing: "-0.01em" }}>
            Who we are
          </h2>
          <p className="lxp-muted" style={{ margin: "0 auto", maxWidth: 720, fontSize: "0.95rem", lineHeight: 1.65 }}>
            SA LexPractiq is a South African Legal Experience Platform (LXP) built by <strong>Nkadimeng Danny Legal &amp; Compliance
            Support</strong>. We exist because too many law graduates finish their LLB without ever drafting a real letter of demand,
            reading a full judgment, or preparing a matter for the CCMA. We close that gap with realistic, mentor-guided practice.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1rem" }}>
          {[
            {
              icon: <Target size={20} />,
              title: "Our mission",
              body: "To make practical legal training accessible to every South African law student, graduate and jobseeker — regardless of which university they attended or whether a firm has offered them articles yet.",
            },
            {
              icon: <Eye size={20} />,
              title: "Our vision",
              body: "A South African legal profession where competence is proven by portfolio, not gatekept by connections — and where recruiters can hire on demonstrated skill.",
            },
            {
              icon: <Flag size={20} />,
              title: "Our goals",
              body: "Publish 100+ authentic SA simulations, verify 10 000 candidate portfolios, and partner with firms, chambers and universities to recognise LexPractiq work as evidence of readiness for articles and pupillage.",
            },
          ].map((v) => (
            <div key={v.title} className="lxp-card">
              <div
                style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: "var(--lxp-accent-glow)", color: "var(--lxp-accent)",
                  display: "grid", placeItems: "center", marginBottom: "0.75rem",
                }}
              >
                {v.icon}
              </div>
              <h3 style={{ margin: "0 0 0.4rem", fontSize: "1.05rem" }}>{v.title}</h3>
              <p className="lxp-muted" style={{ margin: 0, fontSize: "0.9rem", lineHeight: 1.6 }}>{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Internship standing / transparency */}
      <section className="lxp-container" style={{ padding: "2rem 1.25rem" }}>
        <div
          className="lxp-card"
          style={{
            padding: "1.75rem",
            borderLeft: "3px solid var(--lxp-accent)",
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: "1rem",
            alignItems: "start",
          }}
        >
          <div
            style={{
              width: 44, height: 44, borderRadius: 10,
              background: "var(--lxp-accent-glow)", color: "var(--lxp-accent)",
              display: "grid", placeItems: "center",
            }}
          >
            <ShieldAlert size={22} />
          </div>
          <div>
            <h3 style={{ margin: "0 0 0.5rem", fontSize: "1.1rem" }}>Where we stand on internships &amp; articles</h3>
            <p className="lxp-muted" style={{ margin: "0 0 0.6rem", fontSize: "0.9rem", lineHeight: 1.65 }}>
              SA LexPractiq is an <strong>experiential learning platform</strong> — not a law firm, not an accredited PVT/PLT provider,
              and not a substitute for practical vocational training under the Legal Practice Act. We do <strong>not</strong> register
              candidate legal practitioners, sign LPC service contracts, or issue Fidelity Fund cover.
            </p>
            <p className="lxp-muted" style={{ margin: "0 0 0.6rem", fontSize: "0.9rem", lineHeight: 1.65 }}>
              What we <strong>do</strong> give you is verifiable, portfolio-quality evidence of the skills firms and chambers actually
              test at interview: drafting, research, case analysis, ethics and client communication. Our certificates are
              verification-code backed and are designed to <em>strengthen</em> your application for articles, pupillage, learnerships
              and paralegal roles — not to replace them.
            </p>
            <p className="lxp-muted" style={{ margin: 0, fontSize: "0.85rem" }}>
              Firms partnering with us may choose to shortlist LexPractiq-verified candidates for their own articles or vacation
              programmes — those opportunities are governed by the firm's own terms, not ours.
            </p>
          </div>
        </div>
      </section>

      {/* User type strip */}
      <section className="lxp-container" style={{ padding: "3rem 1.25rem" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: "1rem",
          }}
        >
          {[
            {
              icon: <GraduationCap size={22} />,
              title: "Students & Graduates",
              body: "Turn theory into portfolio-ready work while an AI mentor guides you like a real supervising attorney.",
              cta: "Start a simulation",
              to: "/lexpractiq/simulations",
            },
            {
              icon: <Briefcase size={22} />,
              title: "Recruiters & Law Firms",
              body: "Discover candidates by demonstrated skill, not just CVs. Review verified portfolios and invite interviews.",
              cta: "For employers",
              to: "/lexpractiq/for-employers",
            },
            {
              icon: <Users size={22} />,
              title: "Universities & Lecturers",
              body: "Assign simulations to a class, track progress, and standardise practical assessment across cohorts.",
              cta: "For universities",
              to: "/lexpractiq/for-universities",
            },
          ].map((c) => (
            <div key={c.title} className="lxp-card">
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 10,
                  background: "var(--lxp-accent-glow)",
                  color: "var(--lxp-accent)",
                  display: "grid",
                  placeItems: "center",
                  marginBottom: "0.85rem",
                }}
              >
                {c.icon}
              </div>
              <h3 style={{ margin: "0 0 0.5rem", fontSize: "1.1rem" }}>{c.title}</h3>
              <p className="lxp-muted" style={{ fontSize: "0.9rem", margin: "0 0 1rem" }}>
                {c.body}
              </p>
              <Link to={c.to} style={{ color: "var(--lxp-accent)", fontSize: "0.85rem", fontWeight: 600, textDecoration: "none" }}>
                {c.cta} →
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Featured simulations */}
      <section className="lxp-container" style={{ padding: "3rem 1.25rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "end", marginBottom: "1.5rem" }}>
          <div>
            <h2 style={{ fontSize: "1.6rem", margin: "0 0 0.4rem" }}>Featured simulations</h2>
            <p className="lxp-muted" style={{ margin: 0, fontSize: "0.95rem" }}>
              Real South African scenarios across labour, commercial and compliance practice.
            </p>
          </div>
          <Link to="/lexpractiq/simulations" style={{ color: "var(--lxp-accent)", fontSize: "0.9rem", textDecoration: "none" }}>
            View all →
          </Link>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "1rem" }}>
          {data.items.map((s) => (
            <Link
              key={s.slug}
              to="/lexpractiq/simulations/$slug"
              params={{ slug: s.slug }}
              className="lxp-card"
              style={{ textDecoration: "none", display: "block" }}
            >
              <span className="lxp-badge" style={{ marginBottom: "0.6rem" }}>
                {s.practice_area}
              </span>
              <h3 style={{ margin: "0 0 0.5rem", fontSize: "1.05rem", lineHeight: 1.3 }}>{s.title}</h3>
              <p className="lxp-muted" style={{ fontSize: "0.85rem", margin: "0 0 0.85rem", lineHeight: 1.5 }}>
                {s.summary}
              </p>
              <div style={{ display: "flex", gap: "0.75rem", fontSize: "0.75rem", color: "var(--lxp-text-muted)" }}>
                <span>{s.difficulty}</span>
                <span>·</span>
                <span>{Math.round(s.duration_min / 60)}h</span>
                <span>·</span>
                <span>{s.xp_reward} XP</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* How it works quick */}
      <section className="lxp-container" style={{ padding: "3rem 1.25rem" }}>
        <div className="lxp-card" style={{ padding: "2rem" }}>
          <h2 style={{ fontSize: "1.4rem", margin: "0 0 1.5rem", textAlign: "center" }}>How SA LexPractiq works</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1.25rem" }}>
            {[
              { n: "01", t: "Pick a simulation", d: "Browse real SA legal scenarios by practice area and level." },
              { n: "02", t: "Complete tasks", d: "Draft, research and analyse with an AI supervising attorney." },
              { n: "03", t: "Get scored", d: "Rubric feedback across accuracy, reasoning, structure and professionalism." },
              { n: "04", t: "Earn certificate", d: "Verified certificate + portfolio-ready work samples." },
            ].map((s) => (
              <div key={s.n}>
                <div style={{ color: "var(--lxp-accent)", fontSize: "0.8rem", fontWeight: 700, marginBottom: "0.35rem" }}>{s.n}</div>
                <h4 style={{ margin: "0 0 0.4rem", fontSize: "1rem" }}>{s.t}</h4>
                <p className="lxp-muted" style={{ margin: 0, fontSize: "0.85rem" }}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="lxp-container" style={{ padding: "3rem 1.25rem 5rem", textAlign: "center" }}>
        <Award size={36} style={{ color: "var(--lxp-accent)", marginBottom: "1rem" }} />
        <h2 style={{ fontSize: "1.8rem", margin: "0 0 0.75rem" }}>Ready to build your legal experience?</h2>
        <p className="lxp-muted" style={{ margin: "0 auto 1.5rem", maxWidth: 560 }}>
          Free to start. Your first simulation takes under 4 hours and earns you a verified certificate.
        </p>
        <Link to="/lexpractiq/simulations" className="lxp-btn">
          Get started free <CheckCircle2 size={16} />
        </Link>
      </section>
    </>
  );
}
